import * as Model from 'devexpress-dashboard/model';
import {ICustomItemExtension, CustomItemViewer, CustomItemExportInfo} from 'devexpress-dashboard/common';
import {ICustomItemMetaData} from 'devexpress-dashboard/model/items/custom-item/meta';
import dxDataGrid from 'devextreme/ui/data_grid';
import {DashboardBIService} from '../widgets/dashboard/bi-dashboard.service';
import {CustomPropertyMetadata} from 'devexpress-dashboard/model';
import dxForm, {dxFormGroupItem, dxFormSimpleItem} from 'devextreme/ui/form';
import dxPopup from 'devextreme/ui/popup';
import dxButton from 'devextreme/ui/button';
import {SectionOptions} from 'devexpress-dashboard/designer';
import notify from 'devextreme/ui/notify';
import {DashboardGridInputs} from '../models/dashboard/datagrid-input';
import {ListDto} from '../pages/scenario/adjustment/adjustment.service';
import {lastValueFrom} from 'rxjs';
import {GridRowSettings, RowFormatting} from '../models/dashboard/row-formatting';
import countryToCurrency from 'country-to-currency';
import {BiDashboardComponent} from '../widgets/bi-dashboard/bi-dashboard.component';

export interface ActiveViewerInfo {
	viewer: DxDataGridViewer;
	item: Model.CustomItem;
	Identifier: string;
}

const GRID_EXTENSION_NAME = 'CustomDataGridExtension';
const GRID_ID_IDENTIFIER = '_identifier'; // Fix spelling!
const DX_DATA_GRID_COLUMN_DATA_TYPES: string[] = [
	'string',
	'number',
	'date',
	'datetime',
	'boolean',
	'object',
];

function registerProperty(propertyName: string, defaultValue: string, valueType: 'string' | 'number' | 'boolean') {
	const prop: CustomPropertyMetadata = {
		ownerType: Model.CustomItem,
		propertyName,
		defaultValue,
		valueType,
	};
	Model.registerCustomProperty(prop);

	return prop;
}

// Add these new properties and sections for grid configuration
const gridRowSettingsProperty = registerProperty(
	'GridRowSettings',
	'{}',
	'string',
);

// Update the column settings section template
const gridRowSettingsSection: SectionOptions = {
	title: 'Row Configuration',
	items: [
		{
			dataField: 'GridRowSettings',
			label: {visible: false},
			template: (args: {
				component: any;
				data: {dashboardItem: Model.CustomItem};
			}, container) => {
				let templateIdentifier = 'No Identifier Set';
				// 1. Get the form component instance
				const formComponentColumnSettingsSection = args.component;
				const formData = formComponentColumnSettingsSection.option('formData');

				if (formData) {
					templateIdentifier = formData?.model?.Identifier() || 'No Identifier Set';
				}

				// 2. Create configuration button
				const button = document.createElement('div');
				new dxButton(
					button,
					{
						text: '⚙ Row Definition Grid',
						onClick: (e) => {
							// 3. Log all available data
							DxDataGridItemExtension._activeViewers.forEach((value, key) => {
								const {viewer, item, Identifier} = value;

								if (templateIdentifier == Identifier) {
									// 4. Show settings popup
									DxDataGridItemExtension.instance.showGridRowSettingsPopup(item);
								}
							});
						},
					},
				);
				container.appendChild(button);

				return container;
			},
		},
	],
};

// Add these new properties and sections for grid configuration
const gridSettingsProperty = registerProperty(
	'GridSettings',
	'{}',
	'string',
);
// Update the column settings section template
const gridSettingsSection: SectionOptions = {
	title: 'Grid Configuration',
	items: [
		{
			dataField: 'GridSettings',
			label: {visible: false},
			template: (args: {
				component: any;
				data: {dashboardItem: Model.CustomItem};
			}, container) => {
				let templateIdentifier = 'No Identifier Set';
				// 1. Get the form component instance
				const formComponentColumnSettingsSection = args.component;
				const formData = formComponentColumnSettingsSection.option('formData');

				if (formData) {
					templateIdentifier = formData?.model?.Identifier() || 'No Identifier Set';
				}

				// 2. Create configuration button
				const button = document.createElement('div');
				new dxButton(
					button,
					{
						text: '⚙ Configure Grid',
						onClick: (e) => {
							// 3. Log all available data
							DxDataGridItemExtension._activeViewers.forEach((value, key) => {
								const {viewer, item, Identifier} = value;

								if (templateIdentifier == Identifier) {
									// 4. Show settings popup
									DxDataGridItemExtension.instance.showGridSettingsPopup(item);
								}
							});
						},
					},
				);
				container.appendChild(button);

				return container;
			},
		},
	],
};

// Add these new properties and sections
const gridColumnSettingsProperty = registerProperty(
	'GridColumnSettings',
	'{}',
	'string',
);
// Update the column settings section template
const columnSettingsSection: SectionOptions = {
	title: 'Column Configuration',
	items: [
		{
			dataField: 'GridColumnSettings',
			label: {visible: false},
			template: (args: {
				component: any;
				data: {dashboardItem: Model.CustomItem};
			}, container) => {
				let templateIdentifier = 'No Identifier Set';
				// 1. Get the form component instance
				const formComponentColumnSettingsSection = args.component;
				const formData = formComponentColumnSettingsSection.option('formData');

				if (formData) {
					templateIdentifier = formData?.model?.Identifier() || 'No Identifier Set';
				}

				// 2. Create configuration button
				const button = document.createElement('div');
				new dxButton(
					button,
					{
						text: '⚙ Configure Columns',
						onClick: (e) => {
							// 3. Log all available data
							DxDataGridItemExtension._activeViewers.forEach((value, key) => {
								const {viewer, item, Identifier} = value;

								if (templateIdentifier == Identifier) {
									// 4. Show settings popup
									DxDataGridItemExtension.instance.showColumnSettingsPopup(item);
								}
							});
						},
					},
				);
				container.appendChild(button);

				return container;
			},
		},
	],
};

const gridEnableEditableColumns = registerProperty(
	'EnableEditableColumns',
	'false',
	'boolean',
);
const showSaveButtonProperty = registerProperty(
	'ShowSaveButton',
	'false',
	'boolean',
);

// Update the column settings section template
const gridEnableEditableColumnsSection: SectionOptions = {
	title: 'Enable Editable Columns',
	items: [
		{
			dataField: 'EnableEditableColumns',
			label: {visible: false},
			editorType: 'dxCheckBox',
			editorOptions: {
				onValueChanged: (e) => {
					const dashboardItem = DxDataGridItemExtension._currentDashboardItem;

					if (dashboardItem) {
						dashboardItem.customProperties.setValue(
							'EnableEditableColumns',
							e.value,
						);
					}
				},
			},
		},
	],
};

const SvgSaveIcon = `
<svg xmlns="http://www.w3.org/2000/svg" id="SvgSaveIcon"  viewBox="0 0 448 512"><!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.--><path d="M64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-242.7c0-17-6.7-33.3-18.7-45.3L352 50.7C340 38.7 323.7 32 306.7 32L64 32zm0 96c0-17.7 14.3-32 32-32l192 0c17.7 0 32 14.3 32 32l0 64c0 17.7-14.3 32-32 32L96 224c-17.7 0-32-14.3-32-32l0-64zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`;

const SvgGridIcon = `
<svg xmlns="http://www.w3.org/2000/svg" id="${GRID_EXTENSION_NAME}" viewBox="0 0 512 512"><path d="M176 80H80v96h96V80zM80 32h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V80c0-26.5 21.5-48 48-48zm96 304H80v96h96V336zM80 288h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H80c-26.5 0-48-21.5-48-48V336c0-26.5 21.5-48 48-48zM336 80v96h96V80H336zm-48 0c0-26.5 21.5-48 48-48h96c26.5 0 48 21.5 48 48v96c0 26.5-21.5 48-48 48H336c-26.5 0-48-21.5-48-48V80zm96 192c13.3 0 24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H408v64c0 13.3-10.7 24-24 24s-24-10.7-24-24V408H296c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V296c0-13.3 10.7-24 24-24z"/></svg>`;

const gridMeta: ICustomItemMetaData = {
	bindings: [
		// For sum values or getting count values
		{
			propertyName: 'columns',
			dataItemType: 'Measure',
			displayName: 'Columns',
			array: true,
			enableColoring: true,
			emptyPlaceholder: 'Set Columns',
			selectedPlaceholder: 'Configure Columns',
			enableInteractivity: true,
			// i want to implment  here CustomOptionPerColumns or property for each column
		},
		{
			propertyName: 'rows',
			dataItemType: 'Dimension',
			displayName: 'Rows',
			array: true,
			enableColoring: true,
			enableInteractivity: true,
			emptyPlaceholder: 'Set Rows',
			selectedPlaceholder: 'Configure Rows',
		},
		{
			propertyName: 'id',
			dataItemType: 'Dimension',
			displayName: 'ID',
			array: true,
			enableColoring: false,
			emptyPlaceholder: 'Set ID',
			enableInteractivity: true,
			selectedPlaceholder: 'Configure ID',
		},
		{
			propertyName: 'UpdateId',
			dataItemType: 'Dimension',
			displayName: 'Update ID',
			array: false,
			enableColoring: false,
			emptyPlaceholder: 'Set ID',
			enableInteractivity: true,
			selectedPlaceholder: 'Configure ID',
		},
	],
	interactivity: {
		filter: true,
	},
	icon: GRID_EXTENSION_NAME,
	title: 'Custom DataGrid',
}; // ✅ Double cast to avoid TS error;

export class DxDataGridItemExtension implements ICustomItemExtension {
	private static _instance: DxDataGridItemExtension;
	public static _currentDashboardItem: Model.CustomItem | null = null;
	public _dashboardControl: any = null;
	// Add this at the top of your DxDataGridItemExtension class
	private savePopup: dxPopup | null = null;
	private savePopupContainer: HTMLElement | null = null;

	name = GRID_EXTENSION_NAME;
	metaData = gridMeta;

	_dashboardId: string;
	public _componentName: string;

	public triggerDashboardReload(isReloadCall) {
		try {
			if (this._dashboardControl && DxDataGridItemExtension && DxDataGridItemExtension._activeViewers) {
				// Use a more controlled approach to refresh
				requestAnimationFrame(() => {
					try {
						// Clear existing viewers first
						// DxDataGridItemExtension._activeViewers.forEach((viewerData, key) => {
						//     if (viewerData.viewer.dxGridWidget) {
						//         viewerData.viewer.dxGridWidget.dispose();
						//     }
						// });
						if (isReloadCall < 1) {
							// Clear existing viewers first
							DxDataGridItemExtension._activeViewers.forEach((viewerData, key) => {
								if (viewerData.viewer) {
									console.error(
										'LOOPING triggerDashboardReload:',
										key,
										viewerData,
										viewerData.Identifier,
									);
									// this.refreshDashboardGrid(viewerData.viewer);

									// To render the grid content for the first time or re-render it with updated settings
									viewerData.viewer.renderContent(
										viewerData.viewer.container,
										false,
									)
										.then(() => {
											console.log('Grid rendered successfully');
										})
										.catch((error) => {
											console.error(
												'Error rendering grid:',
												error,
											);
										});

									// To refresh the grid content with loading indicator and error handling
									// this.refreshDashboardGrid(viewerData.viewer);

									// viewerData.viewer.re(); // Re-render content
									// viewerData.item.iterateData.apply(viewerData.item);
									// viewerData.viewer.
									// const viewer = DxDataGridItemExtension.getViewer(key);
									//  viewer.renderContent(viewer.container, true);
									// DxDataGridItemExtension.instance._dashboardControl?.reloadData?.();
									// viewerData.viewer.renderContent(viewerData.viewer.container, true);
									// viewerData.viewer.updateDashboardId(this._dashboardId);
								}
							});
						}
						// Then trigger a proper refresh
						// this._dashboardControl.render();
					} catch (error) {
						console.error(
							'Error in dashboard refresh:',
							error,
						);
					}
				});
			}
		} catch (error) {
			console.error(
				'Error triggering dashboard reload:',
				error,
			);
		}
	}

	// Add setter for dashboardId
	set dashboardId(value: string) {
		console.debug(`Updating dashboardId from ${this._dashboardId} to ${value}`);

		if (this._dashboardId !== value) {
			this._dashboardId = value;
			this.updateActiveViewersDashboardId(value);
		}
	}

	get dashboardId(): string {
		return this._dashboardId;
	}

	// Update all active viewers with new dashboardId
	public updateActiveViewersDashboardId(newDashboardId: string) {
		DxDataGridItemExtension._activeViewers.forEach((viewerData, key) => {
			try {
				viewerData.viewer.updateDashboardId(newDashboardId);
			} catch (error) {
				console.error(
					`Failed to update viewer ${key}:`,
					error,
				);
			}
		});
	}

	// Add this to your extension constructor
	public static _activeViewers = new Map<string, ActiveViewerInfo>();
	private dashboardComponentRef: BiDashboardComponent;

	constructor(dashboardControl: any, private dashboardService: DashboardBIService, initialDashboardId?: string, dashboardComponentRef?: BiDashboardComponent) {
		this._dashboardId = initialDashboardId || '';
		DxDataGridItemExtension._instance = this;
		dashboardControl.registerIcon(SvgGridIcon);
		dashboardControl.registerIcon(SvgSaveIcon);
		// this._dashboardId = dashboardId;

		// Add this line to set up the toolbar button
		dashboardControl.findExtension('viewer-api')?.on(
			'itemCaptionToolbarUpdated',
			this.onItemCaptionToolbarUpdated.bind(this),
		);

		this.metaData.customProperties = [
			gridColumnSettingsProperty,
			showSaveButtonProperty,
			gridSettingsProperty,
			gridEnableEditableColumns,
			gridRowSettingsProperty,
		];

		this.metaData.optionsPanelSections = [
			columnSettingsSection,
			gridSettingsSection,
			gridEnableEditableColumnsSection,
			gridRowSettingsSection,
		];

		this._dashboardControl = dashboardControl; // Store the dashboard control reference
		this.createViewerItem = this.createViewerItem.bind(this);
		this.dashboardComponentRef = dashboardComponentRef;
	}

	public static get instance(): DxDataGridItemExtension {
		return DxDataGridItemExtension._instance;
	}

	public static getViewer(dashboardItemId: string): DxDataGridViewer | undefined {
		return this._activeViewers.get(dashboardItemId).viewer;
	}

	// Add cleanup when viewers are disposed
	public cleanupViewer(dashboardItemId: string): void {
		DxDataGridItemExtension._activeViewers.delete(dashboardItemId);
	}

	public createViewerItem = (model: any, element: any, content: any) => {
		const viewer = new DxDataGridViewer(
			model,
			element,
			content,
			this._dashboardId,
			this.dashboardService,
		);
		// Store both viewer and item reference
		const key = model.componentName();
		this._componentName = key;

		if (key) {
			DxDataGridItemExtension._activeViewers.set(
				key,
				{
					viewer: viewer,
					item: model,
					Identifier: model?.customProperties?.Identifier(),
				},
			);
		}

		return viewer;
	};

	public async showColumnSettingsPopup(dashboardItem: Model.CustomItem) {
		if (!dashboardItem || !dashboardItem.dataItems) {
			console.error('Invalid dashboard item provided');

			return;
		}

		let _dashboardGridConfigurationData = await this.dashboardService.dashboardGridConfiguration();
		const popupContainer = document.createElement('div');
		document.body.appendChild(popupContainer);

		try {
			// Get all data items (both measures and dimensions)
			const allDataItems = dashboardItem.dataItems();

			// Get columns from bindings (more reliable than filtering by type)
			const columnBindings = dashboardItem.getBindingValue('columns') || [];
			const rowBindings = dashboardItem.getBindingValue('rows') || [];

			const formContainer = document.createElement('div');

			if (formContainer) {
				formContainer.classList.add('bi-dashboard-custom-dropdown-container');
				formContainer.style.maxHeight = '70vh'; // 70% of viewport height
				formContainer.style.overflowY = 'auto'; // Enable vertical scrolling
				formContainer.style.padding = '10px';
			}

			const settingsValue = dashboardItem.customProperties.getValue('GridColumnSettings') || '{}';
			const customOptions = safelyParseJSON(
				String(settingsValue),
				{},
			);

			// Combine both columns and rows for configuration
			const allColumns = [
				...columnBindings,
				...rowBindings,
			].map((binding) => {
				const dataItem = allDataItems.find((item) => item.uniqueName() === binding.uniqueName());

				return {
					uniqueName: binding.uniqueName(),
					displayName: binding.displayName(),
					dataMember: dataItem?.dataMember(),
					itemType: dataItem?.itemType(),
					isEditable: customOptions[`${binding.uniqueName()}_isEditable`] || false,
					dataType: customOptions[`${binding.uniqueName()}_dataType`] || null,
					alignment: customOptions[`${binding.uniqueName()}_alignment`] || 'left',
					visible: customOptions[`${binding.uniqueName()}_visible`] ?? true,
					backgroundColour: customOptions[`${binding.uniqueName()}_backgroundColour`] ?? '#ffffff',
					fontWeight: customOptions[`${binding.uniqueName()}_fontWeight`] ?? 'normal',
					fontSize: customOptions[`${binding.uniqueName()}_fontSize`] ?? 'normal',
					width: customOptions[`${binding.uniqueName()}_width`] ?? 'auto',
					wordWrap: customOptions[`${binding.uniqueName()}_wordWrap`] ?? true,
					columnFixing: customOptions[`${binding.uniqueName()}_columnFixing`] ?? false,
					AllowSorting: customOptions[`${binding.uniqueName()}_AllowSorting`] ?? true,
					columnFixingPosition: customOptions[`${binding.uniqueName()}_columnFixingPosition`] ?? 'fix',
					dropdownValues: customOptions[`${binding.uniqueName()}_dropdownValues`] || null,
					OrderBy: customOptions[`${binding.uniqueName()}_OrderBy`] || '',
				};
			});

			const popup = new dxPopup(
				popupContainer,
				{
					title: 'Column Settings',
					width: 900,
					height: 'auto',
					maxHeight: '90vh', // Prevent popup from exceeding viewport height
					contentTemplate: (content) => {
						// Define proper types for form items
						const formItems: dxFormSimpleItem[] = allColumns.map((col) => ({
							itemType: 'group',
							caption: col.displayName,
							items: [
								{
									dataField: `${col.uniqueName}_alignment`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Alignment'},
									editorOptions: {
										items: [
											'left',
											'center',
											'right',
										],
										value: col.alignment || 'left',
									},
								},
								{
									dataField: `${col.uniqueName}_backgroundColour`,
									editorType: 'dxColorBox' as const,
									label: {text: 'BackgroundColour'},
									editorOptions: {
										value: col.backgroundColour || '#ffffff',
									},
								},
								{
									dataField: `${col.uniqueName}_fontWeight`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Font Weight'},
									width: 170,
									editorOptions: {
										value: col.fontWeight || 'normal',
										acceptCustomValue: true,
										width: 150,
										dataSource: [
											'normal',
											'bold',
											'bolder',
											'lighter',
										],
									},
								},
								{
									dataField: `${col.uniqueName}_fontSize`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Font Size'},
									editorOptions: {
										value: col.fontSize || 'normal',
										acceptCustomValue: true,
										dataSource: [
											'normal',
											'small',
											'large',
											'x-large',
										],
									},
								},
								{
									dataField: `${col.uniqueName}_width`,
									editorType: 'dxTextBox' as const,
									label: {text: 'Width'},
									editorOptions: {
										value: col.width || 'auto',
									},
								},
								{
									dataField: `${col.uniqueName}_isEditable`,
									editorType: 'dxCheckBox' as const,
									label: {text: 'Editable'},
									editorOptions: {
										value: col.isEditable || false,
									},
								},
								{
									dataField: `${col.uniqueName}_visible`,
									editorType: 'dxCheckBox' as const,
									label: {text: 'Visible'},
									editorOptions: {
										value: col.visible ?? true,
									},
								},
								{
									dataField: `${col.uniqueName}_wordWrap`,
									editorType: 'dxCheckBox' as const,
									label: {text: 'WordWrap'},
									editorOptions: {
										value: col.wordWrap ?? true,
									},
								},
								{
									dataField: `${col.uniqueName}_columnFixing`,
									editorType: 'dxCheckBox' as const,
									label: {text: 'Column Fixing'},
									editorOptions: {
										value: col.columnFixing ?? false,
									},
								},
								{
									dataField: `${col.uniqueName}_columnFixingPosition`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Column Position'},
									editorOptions: {
										value: col.columnFixingPosition || 'fix',
										acceptCustomValue: true,
										dataSource: [
											'fix',
											'left',
											'right',
											'sticky',
											'unfix',
										],
									},
								},
								{
									dataField: `${col.uniqueName}_AllowSorting`,
									editorType: 'dxCheckBox' as const,
									label: {text: 'Allow Sorting'},
									editorOptions: {
										value: col.AllowSorting || false,
									},
								},
								{
									dataField: `${col.uniqueName}_OrderBy`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Order By'},
									editorOptions: {
										value: col.OrderBy || '',
										acceptCustomValue: true,
										dataSource: [
											'',
											'asc',
											'desc',
										],
									},
								},
								{
									dataField: `${col.uniqueName}_dropdownValues`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Dropdown Values'},
									editorOptions: {
										acceptCustomValue: false,
										searchEnabled: true,
										value: col.dropdownValues || null,
										displayExpr: 'listElement',
										valueExpr: 'listId',
										dataSource: _dashboardGridConfigurationData,
									},
								},
								{
									dataField: `${col.uniqueName}_dataType`,
									editorType: 'dxSelectBox' as const,
									label: {text: 'Data Type'},
									editorOptions: {
										acceptCustomValue: false,
										searchEnabled: true,
										value: col.dataType || null,
										dataSource: DX_DATA_GRID_COLUMN_DATA_TYPES,
									},
								},
							],
							colCount: 5,
						}));
						new dxForm(
							formContainer,
							{
								formData: customOptions,
								items: formItems,
								labelLocation: 'top',
								colCount: 1,
							},
						);

						content.appendChild(formContainer);
					},
					toolbarItems: [
						{
							widget: 'dxButton',
							options: {
								text: 'Save',
								onClick: () => {
									try {
										const form = dxForm.getInstance(formContainer);
										const updatedOptions = form?.option('formData') || {};
										dashboardItem.customProperties.setValue(
											'GridColumnSettings',
											JSON.stringify(updatedOptions),
										);
										popup.hide();
									} catch (error) {
										console.error(
											'Error saving grid settings:',
											error,
										);
									}
								},
							},
							toolbar: 'bottom',
							location: 'after',
						},
						{
							widget: 'dxButton',
							options: {
								text: 'Cancel',
								onClick: () => popup.hide(),
							},
							toolbar: 'bottom',
							location: 'after',
						},
					],
					onHidden: () => {
						document.body.removeChild(popupContainer);
					},
					position: {
						my: 'center',
						at: 'center',
						of: window,
					},
				},
			);

			popup.show();
		} catch (error) {
			console.error(
				'Error in showColumnSettingsPopup:',
				error,
			);
		}
	}

	public async showGridSettingsPopup(dashboardItem: Model.CustomItem) {
		if (!dashboardItem || !dashboardItem.dataItems) {
			console.error('Invalid dashboard item provided');

			return;
		}

		let _dashboardGridConfigurationData = await this.dashboardService.LoadDynamicUpdateObject();
		const popupContainer = document.createElement('div');
		document.body.appendChild(popupContainer);

		try {
			// Get all data items (both measures and dimensions)
			const allDataItems = dashboardItem.dataItems();

			const formContainer = document.createElement('div');

			if (formContainer) {
				formContainer.classList.add('bi-dashboard-custom-dropdown-container');
				// formContainer.style.maxHeight = '70vh'; // 70% of viewport height
				// formContainer.style.overflowY = 'auto'; // Enable vertical scrolling
				formContainer.style.padding = '5px';
			}

			const settingsValue = dashboardItem.customProperties.getValue('GridSettings') || '{}';
			const customOptions = safelyParseJSON(
				String(settingsValue),
				{},
			);

			const popup = new dxPopup(
				popupContainer,
				{
					title: 'Grid Settings',
					width: 900,
					height: 'auto',
					scrollByContent: true, // Add to popup options
					maxHeight: 'none', // Prevent popup from exceeding viewport height
					contentTemplate: (content) => {
						// Define proper types for form items
						const formItems: Array<dxFormGroupItem> = [
							{
								itemType: 'group',
								caption: 'Exit Code Settings',
								items: [
									{
										dataField: 'SelectDynamicId', // Single field instead of per-column
										editorType: 'dxSelectBox' as const,
										label: {text: 'Select Exit Code'},
										editorOptions: {
											acceptCustomValue: false,
											showClearButton: true,
											value: customOptions['SelectDynamicId'] || null, // Set initial value if needed
											searchEnabled: true,
											displayExpr: 'listElement',
											valueExpr: 'listId',
											width: 230,
											items: _dashboardGridConfigurationData,
										},
									},
									{
										dataField: 'WaitForExecution',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Wait For Execution'},

										editorOptions: {
											width: 100,
											value: customOptions['WaitForExecution'] || false, // Set initial value if needed
										},
									},
									{
										dataField: 'EnableSaveWithoutChanges',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Enable Save Without Changes'},
										editorOptions: {
											width: 100,
											value: customOptions['EnableSaveWithoutChanges'] || false, // Set initial value if needed
										},
									},
									{
										dataField: 'OnSaveRefresh',
										editorType: 'dxRadioGroup' as const,
										label: {text: 'On Save Refresh'},
										editorOptions: {
											value: customOptions['OnSaveRefresh'] || 'All', // Set initial value if needed
											items: [
												'OnlyCustomGrid',
												'All',
												'None',
											],
											layout: 'horizontal',
											width: 300,
										},
									},
								],
								colCount: 3,
							},
							{
								itemType: 'group',
								caption: 'Scrolling Settings',
								items: [
									{
										dataField: 'ScrollingMode',
										editorType: 'dxSelectBox' as const,
										label: {text: 'mode'},
										editorOptions: {
											value: customOptions['ScrollingMode'] || 'infinite', // Set initial value if needed
											items: [
												'standard',
												'virtual',
												'infinite',
											],
											width: 200,
										},
									},
									{
										dataField: 'ScrollingModeNative',
										editorType: 'dxSelectBox' as const,
										label: {text: 'Native'},
										editorOptions: {
											value: customOptions['ScrollingModeNative'] || 'auto', // Set initial value if needed
											items: [
												'auto',
												'true',
												'false',
											],
											width: 200,
										},
									},
									{
										dataField: 'OnSorting',
										editorType: 'dxRadioGroup' as const,
										label: {text: 'On Sorting'},
										editorOptions: {
											value: customOptions['OnSorting'] || 'multiple', // Set initial value if needed
											items: [
												'single',
												'multiple',
												'none',
											],
											layout: 'horizontal',
											width: 300,
										},
									},
								],
								colCount: 3,
							},
							{
								itemType: 'group',
								caption: 'Filter Settings',
								items: [
									{
										dataField: 'FilterRow',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Enable FilterRow'},
										editorOptions: {
											value: customOptions.hasOwnProperty('FilterRow')
												? customOptions['FilterRow']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'HeaderFilter',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Enable HeaderFilter'},
										editorOptions: {
											value: customOptions.hasOwnProperty('HeaderFilter')
												? customOptions['HeaderFilter']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'HeaderFilterAllowSearch',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Header Filter Allow Search'},
										editorOptions: {
											value: customOptions.hasOwnProperty('HeaderFilterAllowSearch')
												? customOptions['HeaderFilterAllowSearch']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'HeaderFilterAllowSelectAll',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Header Filter Allow Select All'},
										editorOptions: {
											value: customOptions.hasOwnProperty('HeaderFilterAllowSelectAll')
												? customOptions['HeaderFilterAllowSelectAll']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'HeaderFilterHeight',
										editorType: 'dxNumberBox' as const,
										label: {text: 'Header Filter Height'},
										editorOptions: {
											value: customOptions.hasOwnProperty('HeaderFilterHeight')
												? customOptions['HeaderFilterHeight']
												: 300,
											width: 200,
										},
									},
									{
										dataField: 'SearchPanel',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Enable SearchPanel'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SearchPanel')
												? customOptions['SearchPanel']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'SearchPanelHighlightCaseSensitive',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Search CaseSensitive'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SearchPanelHighlightCaseSensitive')
												? customOptions['SearchPanelHighlightCaseSensitive']
												: false,
											width: 200,
										},
									},
									{
										dataField: 'SearchPanelHighlightSearchText',
										editorType: 'dxCheckBox' as const,
										label: {text: 'Highlight Search Text'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SearchPanelHighlightSearchText')
												? customOptions['SearchPanelHighlightSearchText']
												: true,
											width: 200,
										},
									},
									{
										dataField: 'SearchPanelWidth',
										editorType: 'dxNumberBox' as const,
										label: {text: 'Search Panel Width'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SearchPanelWidth')
												? customOptions['SearchPanelWidth']
												: 300,
											width: 200,
										},
									},
									{
										dataField: 'SearchPanelPlaceholder',
										editorType: 'dxTextBox' as const,
										label: {text: 'Search Panel Placeholder'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SearchPanelPlaceholder')
												? customOptions['SearchPanelPlaceholder']
												: 'Search...',
											width: 200,
										},
									},

								],
								colCount: 3,
							},
							{
								itemType: 'group',
								caption: 'Common Settings',
								items: [
									{
										dataField: 'SelectedRowColor',
										editorType: 'dxColorBox' as const,
										label: {text: 'Selected Row Color'},
										editorOptions: {
											value: customOptions.hasOwnProperty('SelectedRowColor')
												? customOptions['SelectedRowColor']
												: '#ffe082',
											width: 200,
										},
									},

								],
								colCount: 3,
							},
						];
						new dxForm(
							formContainer,
							{
								formData: customOptions,
								items: formItems,
								labelLocation: 'top',
								colCount: 1,
							},
						);

						content.appendChild(formContainer);
					},
					toolbarItems: [
						{
							widget: 'dxButton',
							options: {
								text: 'Save',
								onClick: () => {
									try {
										const form = dxForm.getInstance(formContainer);
										const updatedOptions = form?.option('formData') || {};
										dashboardItem.customProperties.setValue(
											'GridSettings',
											JSON.stringify(updatedOptions),
										);
										popup.hide();
									} catch (error) {
										console.error(
											'Error saving grid settings:',
											error,
										);
									}
								},
							},
							toolbar: 'bottom',
							location: 'after',
						},
						{
							widget: 'dxButton',
							options: {
								text: 'Cancel',
								onClick: () => popup.hide(),
							},
							toolbar: 'bottom',
							location: 'after',
						},
					],
					onHidden: () => {
						document.body.removeChild(popupContainer);
					},
					position: {
						my: 'center',
						at: 'center',
						of: window,
					},
				},
			);

			popup.show();
		} catch (error) {
			console.error(
				'Error in showGridSettingsPopup:',
				error,
			);
		}
	}

	public async showGridRowSettingsPopup(dashboardItem: Model.CustomItem) {
		if (!dashboardItem || !dashboardItem.dataItems) {
			console.error('Invalid dashboard item provided');

			return;
		}

		const popupContainer = document.createElement('div');
		document.body.appendChild(popupContainer);

		try {
			// Get columns from bindings
			const columnBindings = dashboardItem.getBindingValue('columns') || [];
			const rowBindings = dashboardItem.getBindingValue('rows') || [];
			const allColumns = [
				...columnBindings,
				...rowBindings,
			].map((binding) => {
				return {
					uniqueName: binding.uniqueName(),
					displayName: binding.displayName(),
				};
			});

			const formContainer = document.createElement('div');
			const gridContainer = document.createElement('div');
			const mainContainer = document.createElement('div');

			mainContainer.style.display = 'flex';
			mainContainer.style.flexDirection = 'column';
			mainContainer.style.gap = '3px';
			mainContainer.appendChild(formContainer);
			mainContainer.appendChild(gridContainer);

			formContainer.classList.add('bi-dashboard-custom-dropdown-container');
			formContainer.style.padding = '3px';

			gridContainer.classList.add('bi-dashboard-custom-grid-container');
			gridContainer.style.padding = '3px';
			gridContainer.style.height = '430px';

			const settingsValue = dashboardItem.customProperties.getValue('GridRowSettings') || '{}';
			let customOptions = safelyParseJSON(
				String(settingsValue),
				{records: []},
			);

			// Initialize records array if it doesn't exist
			if (!customOptions.records) {
				customOptions.records = [];
			}

			// Create data grid for records
			let dataGrid: any;

			const createDataGrid = () => {
				if (dataGrid) {
					dataGrid.dispose();
				}

				dataGrid = new dxDataGrid(
					gridContainer,
					{
						dataSource: customOptions.records,
						keyExpr: 'id',
						editing: {
							mode: 'popup',
							allowUpdating: true,
							allowDeleting: true,
							allowAdding: true,
							popup: {
								title: 'Edit Row Formatting',
								showTitle: true,
								width: '90%',
								height: '90%',
								maxHeight: '540',
								maxWidth: '550',
							},
							form: {
								items: [
									{
										dataField: 'Field',
										editorType: 'dxSelectBox' as const,
										editorOptions: {
											displayExpr: 'displayName',
											valueExpr: 'displayName',
											dataSource: allColumns,
											searchEnabled: true,
											allowEditing: false,
											acceptCustomValue: false,
										},
									},
									{
										dataField: 'BackgroundColour',
										editorType: 'dxColorBox',
										editorOptions: {
											clearButtonVisible: true,
										},
									},
									{dataField: 'FromValue'},
									{
										dataField: 'FromOperation',
										editorType: 'dxSelectBox',
										editorOptions: {
											items: [
												'=',
												'!=',
												'>',
												'<',
												'>=',
												'<=',
												'contains',
												'startsWith',
												'endsWith',
											],
											showClearButton: true,
											clearButtonVisible: true,
										},
									},
									{dataField: 'ToValue'},
									{
										dataField: 'ToOperation',
										editorType: 'dxSelectBox',
										editorOptions: {
											items: [
												'=',
												'!=',
												'>',
												'<',
												'>=',
												'<=',
												'contains',
												'startsWith',
												'endsWith',
											],
											clearButtonVisible: true,
											showClearButton: true,
										},
									},
									{
										dataField: 'FontWeight',
										editorType: 'dxSelectBox',
										editorOptions: {
											items: [
												'normal',
												'bold',
												'bolder',
												'lighter',
											],
											clearButtonVisible: true,
											allowEditing: true,
											acceptCustomValue: true,
											showClearButton: true,
										},
									},
									{
										dataField: 'FontSize',
										editorType: 'dxSelectBox',
										editorOptions: {
											items: [
												'small',
												'medium',
												'large',
												'x-large',
											],
											clearButtonVisible: true,
											allowEditing: true,
											acceptCustomValue: true,
											showClearButton: true,
										},
									},
									{
										dataField: 'RowHeight',
									},
									{
										dataField: 'TextAlignment',
										editorType: 'dxSelectBox',
										editorOptions: {
											items: [
												'left',
												'center',
												'right',
											],
											clearButtonVisible: true,
											showClearButton: true,
										},
									},
									{
										dataField: 'UserEdit',
										editorType: 'dxCheckBox',
									},
									{
										dataField: 'FormatOn',
										editorType: 'dxCheckBox',
									},
								],
							},
						},
						columns: [
							{
								dataField: 'Field',
								caption: 'Field',
								validationRules: [
									{type: 'required'},
								],
								width: 150,
							},
							{
								dataField: 'BackgroundColour',
								caption: 'Background Color',
								width: 120,
							},
							{
								dataField: 'FromValue',
								caption: 'From Value',
								width: 100,
							},
							{
								dataField: 'FromOperation',
								caption: 'From Operation',
								width: 120,
							},
							{
								dataField: 'ToValue',
								caption: 'To Value',
								width: 100,
							},
							{
								dataField: 'ToOperation',
								caption: 'To Operation',
								width: 120,
							},
							{
								dataField: 'FontWeight',
								caption: 'Font Weight',
								width: 80,
							},
							{
								dataField: 'FontSize',
								caption: 'Font Size',
								width: 80,
							},
							{
								dataField: 'RowHeight',
								caption: 'Row Height',
								dataType: 'string',
								width: 80,
							},
							{
								dataField: 'TextAlignment',
								caption: 'Text Alignment',
								width: 100,
							},
							{
								dataField: 'UserEdit',
								caption: 'User Edit',
								dataType: 'boolean',
								width: 80,
							},
							{
								dataField: 'FormatOn',
								caption: 'Enable Formatting',
								dataType: 'boolean',
								width: 80,
							},
							{
								type: 'buttons',
								buttons: [
									'edit',
									'delete',
								],
								width: 60,
							},
						],
						onRowInserting: (e: any) => {
							e.data.id = Date.now();
							// Set default values
							e.data.UserEdit = e.data.UserEdit || true;
							e.data.FormatOn = e.data.FormatOn || true;
							e.data.FontWeight = e.data.FontWeight || 'normal';
							e.data.RowHeight = e.data.RowHeight || 20;
							e.data.FontSize = e.data.FontSize || 'medium';
							e.data.TextAlignment = e.data.TextAlignment || 'left';
						},
						onRowUpdated: () => saveChanges(),
						onRowRemoved: () => saveChanges(),
						showBorders: true,
						allowColumnResizing: true,
						columnAutoWidth: true,
						filterPanel: {visible: false},
						headerFilter: {visible: true},
						searchPanel: {visible: true},
						scrolling: {
							mode: 'virtual',
						},
					},
				);
			};

			// Save changes to custom properties
			const saveChanges = () => {
				dashboardItem.customProperties.setValue(
					'GridRowSettings',
					JSON.stringify(customOptions),
				);
			};

			// Create initial grid
			createDataGrid();

			// Create popup
			const popup = new dxPopup(
				popupContainer,
				{
					title: 'Rows Settings',
					width: '90%',
					height: '90%',
					maxHeight: '560',
					maxWidth: '1450',
					contentTemplate: (content) => {
						content.appendChild(mainContainer);
					},
					toolbarItems: [
						{
							widget: 'dxButton',
							options: {
								text: 'Save',
								onClick: () => {
									const form = dxForm.getInstance(formContainer);
									const updatedOptions = form?.option('formData') || {};

									const finalOptions = {
										...(typeof updatedOptions === 'object' && updatedOptions !== null ? updatedOptions : {}),
										records: customOptions.records,
									};

									dashboardItem.customProperties.setValue(
										'GridRowSettings',
										JSON.stringify(finalOptions),
									);
									popup.hide();
								},
							},
							toolbar: 'bottom',
							location: 'after',
						},
						{
							widget: 'dxButton',
							options: {
								text: 'Cancel',
								onClick: () => popup.hide(),
							},
							toolbar: 'bottom',
							location: 'after',
						},
					],
					onHidden: () => {
						document.body.removeChild(popupContainer);
					},
					position: {
						my: 'center',
						at: 'center',
						of: window,
					},
				},
			);

			popup.show();
		} catch (error) {
			console.error(
				'Error in showGridSettingsPopup:',
				error,
			);
		}
	}

	public static getDashboardItem(componentName: string): Model.CustomItem | null {
		return this._activeViewers.get(componentName)?.item || null;
	}

	// Add this method to your DxDataGridItemExtension class
	private onItemCaptionToolbarUpdated(args: any) {
		// Only handle events for our custom item type

		// Get the dashboard item from args instead of static reference
		const dashboardCurrentItem = args.dashboardItem as Model.CustomItem;

		// if (!dashboardItem || dashboardItem.itemType() !== this.name) return;
		if (!args || !args.dashboardItem || typeof args.dashboardItem.customItemType !== 'function') {
			return;
		}

		if (args.dashboardItem.customItemType() !== this.name) return;

		// Add save button to the toolbar

		if (args.options.stateItems.find((item) => item.name === 'saveChanges')) {
			// If the save button already exists, do not add it again
			let saveChangesIndex = args.options.stateItems.findIndex((item) => item.name === 'saveChanges');
			args.options.stateItems.splice(
				saveChangesIndex,
				1,
			); // Remove existing button
		}

		// Check if the dashboard item has the ShowSaveButton property set to true
		args.options.stateItems.push({
			type: 'button',
			icon: 'SvgSaveIcon',
			tooltip: 'Save Changes',
			hint: 'Save Changes',
			location: 'after',
			name: 'saveChanges',
			click: (e) => {
				let modifiedData: any[] = [];
				let unmodifiedData: any[] = [];

				// Get fresh reference to the item
				const currentItem = DxDataGridItemExtension._activeViewers.get(dashboardCurrentItem.componentName());

				if (!currentItem) {
					notify(
						'Could not find grid item',
						'error',
						2000,
					);

					return;
				}

				let dxDataGridDashboardItem = currentItem.item as Model.CustomItem;
				let dxDataGridDashboardViewer = currentItem.viewer as DxDataGridViewer;
				const dashboardIdentifier = String(currentItem.Identifier);

				// Handle save logic here`

				if (dxDataGridDashboardItem) {
					let allowUpdating = false;
					const gridSettingsValue = safelyParseJSON(
						String(dxDataGridDashboardItem.customProperties.getValue('GridSettings') || '{}'),
						{},
					);
					let EnableSaveWithoutChanges = false;

					if (gridSettingsValue && gridSettingsValue.hasOwnProperty('EnableSaveWithoutChanges')) {
						EnableSaveWithoutChanges = Boolean(gridSettingsValue['EnableSaveWithoutChanges']);
					}

					allowUpdating = Boolean(dxDataGridDashboardItem.customProperties.getValue('EnableEditableColumns'));

					// Get columns from bindings (more reliable than filtering by type)
					const columns = dxDataGridDashboardItem.getBindingValue('columns') || [];
					const dimensions = dxDataGridDashboardItem.getBindingValue('rows') || [];
					const ids = dxDataGridDashboardItem.getBindingValue('id') || [];

					if (ids.length == 0) {
						this.showSaveConfirmation(
							dxDataGridDashboardItem,
							'Warning',
							'No unique ID has been set up.',
							false,
							true,
							300,
						);

						return;
					}

					if (dashboardIdentifier == '') {
						this.showSaveConfirmation(
							dxDataGridDashboardItem,
							'Warning',
							'No unique grid identity has been set up.',
							false,
							true,
							300,
						);

						return;
					}

					if (!allowUpdating) {
						this.showSaveConfirmation(
							dxDataGridDashboardItem,
							'Warning',
							'Enable the grid edit option to save changes.',
							false,
							true,
							300,
						);

						return;
					}

					const gridColumnSettingsValue = safelyParseJSON(
						String(dxDataGridDashboardItem.customProperties.getValue('GridColumnSettings') || '{}'),
						{},
					);
					const isAnyColumnEditable = Object.keys(gridColumnSettingsValue).some((key) => key.endsWith('_isEditable') && gridColumnSettingsValue[key] === true);

					if (!isAnyColumnEditable) {
						this.showSaveConfirmation(
							dxDataGridDashboardItem,
							'Warning',
							'No editable columns have been set up.',
							false,
							true,
							300,
						);

						return;
					}

					if (dxDataGridDashboardViewer._unsavedChanges == false && !EnableSaveWithoutChanges) {
						this.showSaveConfirmation(
							dxDataGridDashboardItem,
							'Warning',
							'No changes have been made to save.',
							false,
							true,
							300,
						);

						return;
					}

					let editableColumnNames: EditableColumnInfo[] = [];
					let editableColumnIds: EditableColumnInfo[] = [];

					// getting Dimension which are editable
					if (dimensions != undefined && dimensions != null && dimensions.length > 0) {
						for (let i = 0; i < dimensions.length; i++) {
							const columnsDimensions = dxDataGridDashboardItem.dataItems().filter((item) => item.itemType() === 'Dimension' && item.uniqueName() === dimensions[i].uniqueName());

							if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_isEditable`] && columnsDimensions && columnsDimensions.length > 0) {
								editableColumnNames.push(
									{
										columnName: columnsDimensions[0].dataMember(),
										fieldName: dimensions[i].uniqueName(),
										isEditable: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_isEditable`],
										isUniqueId: false,
									},
								);
							}
						}
					}

					// getting Measure which are editable
					if (columns != undefined && columns != null && columns.length > 0) {
						for (let i = 0; i < columns.length; i++) {
							// Assuming columns is part of the model's dataItems
							const columnsMeasure = dxDataGridDashboardItem.dataItems().filter((item) => item.itemType() === 'Measure' && item.uniqueName() === columns[i].uniqueName());

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_isEditable`] && columnsMeasure && columnsMeasure.length > 0) {
								editableColumnNames.push(
									{
										columnName: columnsMeasure[0].dataMember(),
										fieldName: columns[i].uniqueName(),
										isEditable: gridColumnSettingsValue[`${columns[i].uniqueName()}_isEditable`],
										isUniqueId: false,
									},
								);
							}
						}
					}

					if (ids != undefined && ids != null && ids.length > 0) {
						for (let i = 0; i < ids.length; i++) {
							const columnsIds = dxDataGridDashboardItem.dataItems().filter((item) => item.uniqueName() === ids[i].uniqueName());

							if (columnsIds && columnsIds.length > 0) {
								editableColumnIds.push(
									{
										columnName: columnsIds[0].dataMember(),
										fieldName: ids[i].uniqueName(),
										isEditable: false,
										isUniqueId: true,
									},
								);
							}
						}
					}

					// this.showSaveConfirmation(dxDataGridDashboardItem, "Save Changes", "Are you sure you want to save the changes?", true, true, 300);

					// Get column names where column editable is true
					const uniqueColumnNames = editableColumnNames
						.map((col) => col.columnName);

					// Get column names where isUniqueId is true
					const uniqueColumnIds = editableColumnIds
						.map((col) => col.columnName);

					const uniqueColumnIdsfieldName = editableColumnIds
						.map((col) => col.fieldName);
					dxDataGridDashboardViewer._changedFcTableRecords.forEach((record: any) => {
						// Create a new object to hold the modified record
						let recordCopy = {};
						uniqueColumnNames.forEach((columnName: string) => {
							let ColName = editableColumnNames.find((col) => col.columnName == columnName);

							// Check if the column name exists in the record
							if (ColName) {
								if (record.hasOwnProperty(ColName.fieldName)) {
									recordCopy[ColName.fieldName] = record[ColName.fieldName];
								}
							}
						});

						uniqueColumnIds.forEach((columnName: string) => {
							let ColName = editableColumnIds.find((col) => col.columnName == columnName);

							// Check if the column name exists in the record
							if (ColName) {
								if (record.hasOwnProperty(ColName.fieldName + '_identifier')) {
									recordCopy[ColName.fieldName] = record[ColName.fieldName + '_identifier'];
								}
							}
						});
						recordCopy['Change'] = 1; // Add the dashboard identifier
						modifiedData.push(recordCopy);
					});

					if (EnableSaveWithoutChanges) {
						// If there are no changed records, we still need to process the existing data
						dxDataGridDashboardViewer._gridData.forEach((record: any) => {
							const index = modifiedData.findIndex((item: any) => {
								return uniqueColumnIdsfieldName.every((itemKey) => {
									const key = itemKey + GRID_ID_IDENTIFIER;
									const itemVal = item[itemKey];
									const recordVal = record[key];

									return itemVal === recordVal;
								});
							});

							if (index === -1) {
								let recordCopy = {};
								// uniqueColumnNames.forEach((columnName: string) => {
								//     let ColName = editableColumnNames.find(col => col.columnName == columnName);
								//     // Check if the column name exists in the record
								//     if (ColName) {
								//         if (record.hasOwnProperty(ColName.fieldName)) {
								//             recordCopy[ColName.fieldName] = record[ColName.fieldName];
								//         }
								//     }
								// });

								uniqueColumnIds.forEach((columnName: string) => {
									let ColName = editableColumnIds.find((col) => col.columnName == columnName);

									// Check if the column name exists in the record
									if (ColName) {
										if (record.hasOwnProperty(ColName.fieldName + '_identifier')) {
											recordCopy[ColName.fieldName] = record[ColName.fieldName + '_identifier'];
										}
									}
								});
								recordCopy['Change'] = 0; // Add the dashboard identifier
								unmodifiedData.push(recordCopy);
							}
						});
					}
				}

				const inputData = new DashboardGridInputs();
				inputData.dashbaordId = this._dashboardId;
				inputData.Identifier = dashboardIdentifier;
				inputData.values = JSON.stringify(modifiedData);
				inputData.unmfyvalues = JSON.stringify(unmodifiedData);
				// This will save any pending changes first
				// const result = dxDataGridDashboardViewer.dxGridWidget.saveEditData();
				const gridSettings = safelyParseJSON(
					String(dxDataGridDashboardItem.customProperties.getValue('GridSettings') || '{}'),
					{OnSaveRefresh: 'None'},
				);

				// Show Loader in the grid
				let OnSaveRefresh = 'none';

				if (gridSettings.OnSaveRefresh && typeof gridSettings.OnSaveRefresh === 'string') {
					OnSaveRefresh = gridSettings.OnSaveRefresh.toLowerCase();
				}

				if (OnSaveRefresh == 'onlycustomgrid') {
					if (dxDataGridDashboardViewer.dxGridWidget) {
						dxDataGridDashboardViewer.dxGridWidget.beginCustomLoading('Saving...');
					}
				} else {
					if (this.dashboardComponentRef) {
						this.dashboardComponentRef.showDashboardLoader();
					}
				}

				dxDataGridDashboardViewer.dxGridWidget.saveEditData().then(() => {
					// Call the service to save the data
					this.dashboardService.saveDataGridData(inputData).subscribe(async (response: any) => {
						try {
							if (response && response.flag != undefined && response.flag != null) {
								if (response.flag == 1) {
									dxDataGridDashboardViewer._changedFcTableRecords = []; // Clear the changed records after successful save

									if (OnSaveRefresh == 'onlycustomgrid') {
										// Refresh a specific item by name
										await this._dashboardControl?.refresh(dashboardCurrentItem.componentName());
									} else if (OnSaveRefresh == 'all') {
										await this._dashboardControl?.reloadData();
									}

									if (dxDataGridDashboardViewer.dxGridWidget) {
										dxDataGridDashboardViewer.dxGridWidget.repaint();
									}

									notify(
										response.msg,
										'success',
										2000,
									);
								} else {
									notify(
										response.msg,
										'error',
										2000,
									);
								}
							} else {
								notify(
									'Error saving data',
									'error',
									2000,
								);
							}

							// Save completed
							dxDataGridDashboardViewer.dxGridWidget.endCustomLoading();
							this.dashboardComponentRef.hideDashboardLoader();
						} catch (ex) {
							console.error(
								'Error in save response handling:',
								ex,
							);
							dxDataGridDashboardViewer.dxGridWidget.endCustomLoading();
							this.dashboardComponentRef.hideDashboardLoader();
						}
					});
				});
			},
			index: 0, // Places button at the start of the toolbar
		});
	}

	// Place this function outside the class definition (after the class ends)
	public refreshDashboardGrid(viewer: DxDataGridViewer) {
		try {
			// Show loading state
			if (viewer.dxGridWidget) {
				viewer.dxGridWidget.option(
					'loadingIndicator.show',
					true,
				);
			}

			// Full re-render
			viewer.renderContent(
				viewer.container,
				true,
			);
			// viewer.
			// viewer.updateContent.apply(viewer);
			// Alternative light refresh:
			// if (viewer.dxGridWidget) {
			//     viewer.dxGridWidget.option('dataSource', viewer._getDataSource());
			//     viewer.dxGridWidget.refresh();
			// }
		} catch (error) {
			console.error(
				'Refresh failed:',
				error,
			);
		} finally {
			// Hide loading state
			if (viewer.dxGridWidget) {
				viewer.dxGridWidget.option(
					'loadingIndicator.show',
					false,
				);
			}
		}
	}

	checkForDuplicateEditableColumns(editableColumn: any[]): boolean {
		const seen = new Set();

		for (const item of editableColumn) {
			if (item.isEditable) {
				const key = item.columnName;

				if (seen.has(key)) {
					console.error(`Error: Duplicate editable column found for columnName: ${key}`);

					return false;
				}

				seen.add(key);
			}
		}

		return true;
	}

	// In your DxDataGridItemExtension class:
	private showSaveConfirmation(dashboardItem: Model.CustomItem, title?: string, msg?: string, enableOk: boolean = true, enableCancel: boolean = true, width: number = 300) {
		// Clean up any existing popup
		if (this.savePopupContainer && document.body.contains(this.savePopupContainer)) {
			document.body.removeChild(this.savePopupContainer);
		}

		// Create new container
		this.savePopupContainer = document.createElement('div');
		document.body.appendChild(this.savePopupContainer);

		// Create buttons container upfront
		const buttonsContainer = document.createElement('div');
		buttonsContainer.id = 'popupButtons';
		buttonsContainer.style.textAlign = 'right';
		buttonsContainer.style.marginTop = '20px';

		// Create content element
		const content = document.createElement('div');
		content.style.padding = '20px';
		content.innerHTML = '<div style="margin-bottom: 20px;">' + msg + '</div>';
		content.appendChild(buttonsContainer);

		// Create buttons
		const okButton = document.createElement('div');
		const cancelButton = document.createElement('div');
		cancelButton.style.marginLeft = '10px';
		cancelButton.style.display = 'inline-block';

		if (enableOk) {
			buttonsContainer.appendChild(okButton);
		}

		if (enableCancel) {
			buttonsContainer.appendChild(cancelButton);
		}

		this.savePopup = new dxPopup(
			this.savePopupContainer,
			{
				width: width,
				height: 'auto',
				showTitle: true,
				title: title,
				visible: true,
				dragEnabled: false,
				closeOnOutsideClick: false,
				contentTemplate: () => content,
				onHidden: () => {
					if (this.savePopupContainer && document.body.contains(this.savePopupContainer)) {
						document.body.removeChild(this.savePopupContainer);
					}

					this.savePopup = null;
					this.savePopupContainer = null;
				},
			} as any,
		);

		// Initialize buttons
		if (enableOk) {
			new dxButton(
				okButton,
				{
					text: 'OK',
					type: 'default',
					stylingMode: 'contained',
					onClick: () => {
						this.savePopup?.hide();
						// OK button logic
					},
				},
			);
		}

		if (enableCancel) {
			new dxButton(
				cancelButton,
				{
					text: 'Cancel',
					onClick: () => {
						this.savePopup?.hide();
						// Cancel button logic
					},
				},
			);
		}
	}

	public findViewerForItem(dashboardItem: Model.CustomItem): DxDataGridViewer | null {
		const currentItem = DxDataGridItemExtension._currentDashboardItem as any;

		if (currentItem?.id() === (dashboardItem as any).id()) {
			return currentItem.viewer as DxDataGridViewer;
		}

		return null;
	}
}

class DxDataGridViewer extends CustomItemViewer {
	public dxGridWidget?: dxDataGrid;
	exportingImageData?: string;
	public _customModel: Model.CustomItem;

	public _gridData: any[] = [];
	public _changedFcTableRecords: any[] = [];
	public _unsavedChanges = false; // Flag to track unsaved changes;

	// Add method to update dashboardId
	updateDashboardId(newDashboardId: string) {
		//   this._dashboardId = newDashboardId;
	}

	// In DxDataGridViewer class
	public refreshGrid() {
		if (this.dxGridWidget) {
			try {
				// Clear the existing data
				this._gridData = [];

				// Re-fetch the data source
				const newData = this._getDataSource();

				// Update the grid if it exists
				if (this.dxGridWidget) {
					this.dxGridWidget.option(
						'dataSource',
						newData,
					);
					this.dxGridWidget.refresh();
				}

				// Clear any unsaved changes
				this._changedFcTableRecords = [];
				this._unsavedChanges = false;
			} catch (error) {
				console.error(
					'Error refreshing grid:',
					error,
				);
			}
		}
	}

	constructor(model: any, container: any, options: any, dashboardId: string, private dashboardService: DashboardBIService) {
		super(
			model,
			container,
			options,
		);
		this._customModel = model;
		this._initialize();
	}

	private _getDataSource(): any[] {
		this._gridData = [];

		try {
			let gridRowSettings = this._customModel.customProperties.getValue('GridRowSettings');
			let parsedSettings: GridRowSettings = {records: []};

			try {
				parsedSettings = gridRowSettings != undefined && gridRowSettings != null && gridRowSettings != ''
					? {
						records: safelyParseJSON(
							String(gridRowSettings),
							{records: []},
						).records.map((r: any) => new RowFormatting(r)),
					}
					: {records: []};
			} catch (e) {
				parsedSettings = {records: []};
			}

			let columns = this.getBindingValue('columns') || [];
			let dimensionsRows = this.getBindingValue('rows') || [];
			let ids = this.getBindingValue('id') || [];

			if (ids && ids.length > 0) {
				for (let i = 0; i < ids.length; i++) {
					parsedSettings.records.find((record) => {
						if (record.Field === ids[i].displayName()) {
							record.DataItemField = ids[i].uniqueName();
						}
					});
				}
			}

			if (columns && columns.length > 0) {
				for (let i = 0; i < columns.length; i++) {
					parsedSettings.records.find((record) => {
						if (record.Field === columns[i].displayName()) {
							record.DataItemField = columns[i].uniqueName();
						}
					});
				}
			}

			if (dimensionsRows && dimensionsRows.length > 0) {
				for (let i = 0; i < dimensionsRows.length; i++) {
					parsedSettings.records.find((record) => {
						if (record.Field === dimensionsRows[i].displayName()) {
							record.DataItemField = dimensionsRows[i].uniqueName();
						}
					});
				}
			}

			// Check if the columns array is empty or not
			if (dimensionsRows == undefined || dimensionsRows == null) {
				dimensionsRows = [];
			}

			if (columns == undefined || columns == null) {
				columns = [];
			}

			if (ids == undefined || ids == null) {
				ids = [];
			}

			// Check if iterateData method exists
			if (!this.iterateData) {
				console.error('iterateData method is not available');

				return this._gridData;
			}

			// Verify iterateData exists and is a function
			if (typeof this.iterateData !== 'function') {
				console.error('iterateData is not available or not a function');

				return this._gridData;
			}

			const hasCols = columns.length > 0;
			let uniqueRowIdCounter: number = 1;
			this.iterateData((dataRow) => {
				try {
					// Check if dataRow is valid
					if (!dataRow) return;
					const rowObj: any = {
						clientDataRow: dataRow,
					};

					let DataCount = 0;

					// Add ids values
					if (ids && ids.length > 0) {
						const idValues = dataRow.getValue('id');

						if (Array.isArray(idValues)) {
							DataCount = idValues.length;

							for (let i = 0; i < idValues.length; i++) {
								rowObj[ids[i].uniqueName() + GRID_ID_IDENTIFIER] = idValues[i];
							}
						}
					}

					// Add dimension values
					if (dimensionsRows && dimensionsRows.length > 0) {
						const dimensionValues = dataRow.getValue('rows');

						if (Array.isArray(dimensionValues)) {
							DataCount = dimensionValues.length;

							for (let i = 0; i < dimensionValues.length; i++) {
								rowObj[dimensionsRows[i].uniqueName()] = dimensionValues[i];
							}
						}
					}

					// Add column values
					if (hasCols && columns.length > 0) {
						const values = dataRow.getValue('columns');

						if (Array.isArray(values)) {
							DataCount = values.length;

							for (let i = 0; i < values.length; i++) {
								rowObj[columns[i].uniqueName()] = values[i];
							}
						}
					}

					if (DataCount > 0) {
						rowObj['DataKeyRowIndex'] = uniqueRowIdCounter++;
					}

					this._gridData.push(rowObj);
				} catch (e) {
					console.error(
						'Inner Error Error building data source',
						e,
					);
				}
			});

			if (parsedSettings.records?.length > 0) {
				const compare = (value: any, compareValue: any, operation: string): boolean => {
					switch (operation) {
						case '=': return value == compareValue;
						case '!=': return value != compareValue;
						case '>': return value > compareValue;
						case '<': return value < compareValue;
						case '>=': return value >= compareValue;
						case '<=': return value <= compareValue;
						case 'contains': return String(value).includes(String(compareValue));
						case 'startsWith': return String(value).startsWith(String(compareValue));
						case 'endsWith': return String(value).endsWith(String(compareValue));
						default: return true; // If operation is null/blank, treat as always true
					}
				};

				parsedSettings.records.forEach((record) => {
					if (!record?.DataItemField) return;

					if (record.FormatOn) {
						this._gridData = this._gridData.map((row) => {
							const cellValue = row[record.DataItemField];
							if (cellValue === undefined) return row;

							const fromCondition = record.FromValue && record.FromOperation
								? compare(
									cellValue,
									record.FromValue,
									record.FromOperation,
								)
								: true;

							const toCondition = record.ToValue && record.ToOperation
								? compare(
									cellValue,
									record.ToValue,
									record.ToOperation,
								)
								: true;

							if (fromCondition && toCondition) {
								row.styles = row.styles || '';
								row.UserEdit = record.UserEdit || false;
								if (record.BackgroundColour) row.styles += `background-color: ${record.BackgroundColour} !important; `;
								if (record.FontWeight) row.styles += `font-weight: ${record.FontWeight} !important; `;
								if (record.FontSize) row.styles += `font-size: ${record.FontSize} !important; `;
								if (record.RowHeight) row.styles += `height: ${(record.RowHeight.toString().toLowerCase()
									.includes('px')
									? record.RowHeight
									: record.RowHeight + 'px')} !important; `;
								if (record.TextAlignment) row.styles += `text-align: ${record.TextAlignment} !important; `;
							}

							return row;
						});
					}
				});
			}
		} catch (e) {
			console.error(
				'Error building data source',
				e,
			);
		}

		return this._gridData;
	}

	private async _getColumnsConfig(): Promise<any[]> {
		const config: any[] = [];

		try {
			// Early return if model isn't ready
			if (!this._customModel || !this._customModel.dataItems) {
				return [];
			}

			// Safely get bindings with fallbacks
			// Get the grid column settings from custom properties
			const gridColumnSettingsValue = safelyParseJSON(
				String(this._customModel.customProperties.getValue('GridColumnSettings') || '{}'),
				{},
			);

			// Extract all dropdownValues
			const dropdownValues = new Set<any>();

			if (gridColumnSettingsValue != undefined || gridColumnSettingsValue != null) {
				for (const key in gridColumnSettingsValue) {
					if (key.endsWith('_dropdownValues')) {
						const value = gridColumnSettingsValue[key];

						// If value is an array, add each item; otherwise, add the value itself
						if (Array.isArray(value)) {
							value.forEach((v) => dropdownValues.add(v));
						} else {
							dropdownValues.add(value);
						}
					}
				}
			}

			// Convert to array if needed
			const distinctValuesArray = Array.from(dropdownValues);

			// Get dropdown data FIRST
			const dataList = await lastValueFrom(
				this.dashboardService.dashboardGridDropdownData(distinctValuesArray),
			);
			const columns = this.getBindingValue('columns') || [];
			const dimensions = this.getBindingValue('rows') || [];
			const ids = this.getBindingValue('id') || [];
			let style = '', width = '', FieldName = '';

			if (this._customModel && this._customModel.dataItems && this._customModel.dataItems()) {
				if (ids && ids.length > 0) {
					for (let i = 0; i < ids.length; i++) {
						const columnsDimensions = this._customModel.dataItems().filter((item) => item.itemType() === 'Dimension' && item.uniqueName() === ids[i].uniqueName());
						FieldName = ids[i].displayName();

						if (columnsDimensions && columnsDimensions.length > 0 && columnsDimensions[0].dataMember()) {
							FieldName = columnsDimensions[0].dataMember();
						}

						style = '';

						if (gridColumnSettingsValue[`${ids[i].uniqueName()}_backgroundColour`]) {
							style += gridColumnSettingsValue[`${ids[i].uniqueName()}_backgroundColour`] ? 'background-color:' + gridColumnSettingsValue[`${ids[i].uniqueName()}_backgroundColour`] + ' !important;' : '';
						}

						if (gridColumnSettingsValue[`${ids[i].uniqueName()}_fontWeight`]) {
							style += gridColumnSettingsValue[`${ids[i].uniqueName()}_fontWeight`] ? 'font-weight:' + gridColumnSettingsValue[`${ids[i].uniqueName()}_fontWeight`] + ' !important;' : '';
						}

						if (gridColumnSettingsValue[`${ids[i].uniqueName()}_fontSize`]) {
							style += gridColumnSettingsValue[`${ids[i].uniqueName()}_fontSize`] ? 'font-size:' + gridColumnSettingsValue[`${ids[i].uniqueName()}_fontSize`] + ' !important;' : '';
						}

						width = '';

						if (gridColumnSettingsValue[`${ids[i].uniqueName()}_width`] && gridColumnSettingsValue[`${ids[i].uniqueName()}_width`].trim().toLowerCase() == 'auto') {
							width = gridColumnSettingsValue[`${ids[i].uniqueName()}_width`];
						}

						const columnConfig: any = {
							dataField: ids[i].uniqueName() + GRID_ID_IDENTIFIER,
							caption: ids[i].displayName(),
							allowSorting: gridColumnSettingsValue[`${ids[i].uniqueName()}_AllowSorting`] ?? true,
							allowEditing: gridColumnSettingsValue[`${ids[i].uniqueName()}_isEditable`] ?? false,
							name: FieldName + GRID_ID_IDENTIFIER,
							sortOrder: gridColumnSettingsValue[`${ids[i].uniqueName()}_OrderBy`] || '',
							alignment: gridColumnSettingsValue[`${ids[i].uniqueName()}_alignment`] || 'left',
							visible: gridColumnSettingsValue[`${ids[i].uniqueName()}_visible`] ?? false,
							cssClass: (gridColumnSettingsValue[`${ids[i].uniqueName()}_wordWrap`] ? ' WrappedColumnClass' : ''),
							style: style,
							width: width,
						};

						config.push(columnConfig);
					}
				}

				if (dimensions != undefined && dimensions != null && dimensions.length > 0) {
					for (let i = 0; i < dimensions.length; i++) {
						const columnsDimensions = this._customModel.dataItems().filter((item) => item.itemType() === 'Dimension' && item.uniqueName() === dimensions[i].uniqueName());
						FieldName = dimensions[i].displayName();

						if (columnsDimensions && columnsDimensions.length > 0 && columnsDimensions[0].dataMember()) {
							FieldName = columnsDimensions[0].dataMember();
						}

						style = '';

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_backgroundColour`]) {
							style += gridColumnSettingsValue[`${dimensions[i].uniqueName()}_backgroundColour`] ? 'background-color:' + gridColumnSettingsValue[`${dimensions[i].uniqueName()}_backgroundColour`] + ' !important;' : '';
						}

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontWeight`]) {
							style += gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontWeight`] ? 'font-weight:' + gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontWeight`] + ' !important;' : '';
						}

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontSize`]) {
							style += gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontSize`] ? 'font-size:' + gridColumnSettingsValue[`${dimensions[i].uniqueName()}_fontSize`] + ' !important;' : '';
						}

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_alignment`]) {
							style += gridColumnSettingsValue[`${dimensions[i].uniqueName()}_alignment`] ? 'text-align:' + gridColumnSettingsValue[`${dimensions[i].uniqueName()}_alignment`] + ' !important;' : 'left';
						}

						width = '';

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_width`] && gridColumnSettingsValue[`${dimensions[i].uniqueName()}_width`].trim().toLowerCase() == 'auto') {
							width = gridColumnSettingsValue[`${dimensions[i].uniqueName()}_width`];
						}

						const columnConfig: any = {
							dataField: dimensions[i].uniqueName(),
							caption: dimensions[i].displayName(),
							allowSorting: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_AllowSorting`] ?? true,
							allowEditing: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_isEditable`] ?? false,
							sortOrder: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_OrderBy`] || '',
							name: FieldName,
							alignment: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_alignment`] || 'left',
							visible: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_visible`] ?? true,
							cssClass: (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_wordWrap`] ? ' WrappedColumnClass' : ''),
							style: style,
							width: width,
							fixed: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_columnFixing`] ?? false,
							fixedPosition: gridColumnSettingsValue[`${dimensions[i].uniqueName()}_columnFixingPosition`] ?? 'normal',
							cellTemplate: (cellElement, cellInfo) => {
								if (cellInfo.rowType === 'data') {
									let text = '';
									const container = document.createElement('div');
									container.className = 'cell custom-cell';

									if (cellInfo.data && cellInfo.data.styles) {
										container.style.cssText = cellInfo.data.styles || '';
									}

									if (cellInfo.column.dataType === 'date' || cellInfo.column.dataType === 'datetime') {
										// If the cell value is present and not 'null' or zero
										if (cellInfo.value && cellInfo.value instanceof Date && !isNaN(cellInfo.value.getTime())) {
											text = cellInfo.value.toLocaleDateString();
										} else if (cellInfo.value && typeof cellInfo.value === 'string' && cellInfo.value.trim() !== '') {
											// Try convert string to Date safely
											const date = new Date(cellInfo.value);
											text = !isNaN(date.getTime()) ? date.toLocaleDateString() : '';
										}

										container.textContent = text;
									} else {
										container.textContent = cellInfo.text;
									}

									cellElement.appendChild(container);
								}
							},
						};

						// Create a numeric format object that matches the expected type
						let dataType = '';

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dataType`] && gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dataType`] != '') {
							dataType = gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dataType`];
						}

						if (dataType === 'date' || dataType === 'datetime') {
							// columnConfig.dataTypeTest = dataType;
							try {
								let groupInterval = '';

								if (typeof columnsDimensions[0]['dateTimeGroupInterval'] === 'function') {
									groupInterval = columnsDimensions[0]['dateTimeGroupInterval']();
								}

								const dateTimeFormat = columnsDimensions[0].dateTimeFormat;  // object holding formatting info
								const devExtremeFormat = this.getDevExtremeDateFormat(
									groupInterval,
									dateTimeFormat,
								);

								if (devExtremeFormat && devExtremeFormat[0]) {
									// columnConfig.dataType = dataType;
									if (devExtremeFormat[1]) {
									}
								} else {
									columnConfig.dataType = 'string';

									if (groupInterval === '') {

									}
								}
							} catch (e) {
								console.error(
									'Error building dataType datetime, date',
									e,
								);
							}
						}

						if (dataType === 'number') {
							try {
								// Use the numeric format object
								let currencyCultureName = this.getIsoCurrencyFromLocale(columnsDimensions[0].numericFormat.currencyCultureName());
								columnConfig.dataType = 'number';
								columnConfig.format = {};

								if (columnsDimensions[0] && columnsDimensions[0].numericFormat && columnConfig.format) {
									let type = '';

									if (columnsDimensions[0].numericFormat.formatType()) {
										type = columnsDimensions[0].numericFormat.formatType();

										if (type.toLowerCase() === 'number') {
											columnConfig.format.type = 'fixedPoint';
										} else if (type.toLowerCase() === 'scientific') {
											columnConfig.format.type = 'exponential';
										} else if (type.toLowerCase() !== 'auto' && type.toLowerCase() !== 'general') {
											columnConfig.format.type = columnsDimensions[0].numericFormat.formatType();
										}
									}

									if (type.toLowerCase() !== 'auto' && type.toLowerCase() !== 'general') {
										if (columnsDimensions[0].numericFormat.precision()) {
											columnConfig.format.minimumSignificantDigits = columnsDimensions[0].numericFormat.precision();
										}

										let unit = columnsDimensions[0].numericFormat.unit();

										if (unit && type.toLowerCase() !== 'scientific' && unit.toLowerCase() !== 'auto'
											&& unit.toLowerCase() !== 'ones'
											&& type.toLowerCase() !== 'percent' && type.toLowerCase() !== 'auto') {
											columnConfig.format.type = unit;
										}

										if (type.toLowerCase() === 'number' && unit.toLowerCase() === 'ones') {
											// columnConfig.format.type = unit;
										}

										if (columnsDimensions[0].numericFormat.includeGroupSeparator() && type.toLowerCase() !== 'scientific') {
											columnConfig.format.useGrouping = columnsDimensions[0].numericFormat.includeGroupSeparator();
										}

										if (columnsDimensions[0].numericFormat.currencyCultureName() && currencyCultureName && type.toLowerCase() === 'currency') {
											columnConfig.format.currency = currencyCultureName;
										}

										if (columnsDimensions[0].numericFormat.precision()) {
											columnConfig.format.precision = columnsDimensions[0].numericFormat.precision();
											columnConfig.precision = columnsDimensions[0].numericFormat.precision();
										}
									}
								}
							} catch (e) {
								console.error(
									'Error building dataType number',
									e,
								);
							}
						}

						if (gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dropdownValues`] && gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dropdownValues`] != '') {
							let _dashboardGridConfigurationData = dataList.filter((item: any) => {
								return item.listId === gridColumnSettingsValue[`${dimensions[i].uniqueName()}_dropdownValues`];
							});

							if (_dashboardGridConfigurationData && _dashboardGridConfigurationData.length > 0) {
								columnConfig.lookup = {
									dataSource: _dashboardGridConfigurationData,
									valueExpr: 'listElement',     // adjust as per your data shape
									displayExpr: 'listElement',     // adjust as per your data shape
								};
							}
						}

						config.push(columnConfig);
					}
				}

				if (columns != undefined && columns != null && columns.length > 0) {
					for (let i = 0; i < columns.length; i++) {
						// Assuming columns is part of the model's dataItems
						const columnsMeasure = this._customModel.dataItems().filter((item) => item.itemType() === 'Measure' && item.uniqueName() === columns[i].uniqueName());

						FieldName = columns[i].displayName();

						if (columnsMeasure && columnsMeasure.length > 0) {
							if (columnsMeasure && columnsMeasure.length > 0 && columnsMeasure[0].dataMember()) {
								FieldName = columnsMeasure[0].dataMember();
							}

							style = '';

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_backgroundColour`]) {
								style += gridColumnSettingsValue[`${columns[i].uniqueName()}_backgroundColour`] ? 'background-color:' + gridColumnSettingsValue[`${columns[i].uniqueName()}_backgroundColour`] + ' !important;' : '';
							}

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_fontWeight`]) {
								style += gridColumnSettingsValue[`${columns[i].uniqueName()}_fontWeight`] ? 'font-weight:' + gridColumnSettingsValue[`${columns[i].uniqueName()}_fontWeight`] + ' !important;' : '';
							}

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_fontSize`]) {
								style += gridColumnSettingsValue[`${columns[i].uniqueName()}_fontSize`] ? 'font-size:' + gridColumnSettingsValue[`${columns[i].uniqueName()}_fontSize`] + ' !important;' : '';
							}

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_alignment`]) {
								style += gridColumnSettingsValue[`${columns[i].uniqueName()}_alignment`] ? 'text-align:' + gridColumnSettingsValue[`${columns[i].uniqueName()}_alignment`] + ' !important;' : 'left';
							}

							width = '';

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_width`] && gridColumnSettingsValue[`${columns[i].uniqueName()}_width`].trim().toLowerCase() == 'auto') {
								width = gridColumnSettingsValue[`${columns[i].uniqueName()}_width`];
							}

							const columnConfig: any = {
								dataField: columns[i].uniqueName(),
								caption: columns[i].displayName(),
								allowSorting: gridColumnSettingsValue[`${columns[i].uniqueName()}_AllowSorting`] ?? true,
								allowEditing: gridColumnSettingsValue[`${columns[i].uniqueName()}_isEditable`] ?? false,
								sortOrder: gridColumnSettingsValue[`${columns[i].uniqueName()}_OrderBy`] || '',
								name: FieldName,
								alignment: gridColumnSettingsValue[`${columns[i].uniqueName()}_alignment`] || 'left',
								visible: gridColumnSettingsValue[`${columns[i].uniqueName()}_visible`] ?? true,
								cssClass: (gridColumnSettingsValue[`${columns[i].uniqueName()}_wordWrap`] ? ' WrappedColumnClass' : ''),
								style: style,
								width: width,
								fixed: gridColumnSettingsValue[`${columns[i].uniqueName()}_columnFixing`] ?? false,
								fixedPosition: gridColumnSettingsValue[`${columns[i].uniqueName()}_columnFixingPosition`] ?? 'fix',
								cellTemplate: (cellElement, cellInfo) => {
									if (cellInfo.rowType === 'data') {
										let text = '';
										const container = document.createElement('div');
										container.className = 'cell custom-cell';

										if (cellInfo.data && cellInfo.data.styles) {
											container.style.cssText = cellInfo.data.styles || '';
										}

										if (cellInfo.column.dataType === 'date' || cellInfo.column.dataType === 'datetime') {
											// If the cell value is present and not 'null' or zero
											if (cellInfo.value && cellInfo.value instanceof Date && !isNaN(cellInfo.value.getTime())) {
												text = cellInfo.value.toLocaleDateString();
											} else if (cellInfo.value && typeof cellInfo.value === 'string' && cellInfo.value.trim() !== '') {
												// Try convert string to Date safely
												const date = new Date(cellInfo.value);
												text = !isNaN(date.getTime()) ? date.toLocaleDateString() : '';
											}

											container.textContent = text;
										} else {
											container.textContent = cellInfo.text;
										}

										cellElement.appendChild(container);
									}
								},
							};

							// Create a numeric format object that matches the expected type
							let dataType = '';

							if (gridColumnSettingsValue[`${columns[i].uniqueName()}_dataType`] && gridColumnSettingsValue[`${columns[i].uniqueName()}_dataType`] != '') {
								dataType = gridColumnSettingsValue[`${columns[i].uniqueName()}_dataType`];
							}

							if (dataType === 'date' || dataType === 'datetime') {
								// columnConfig.dataTypeTest = dataType;
								try {
									let groupInterval = '';

									if (typeof columnsMeasure[0]['dateTimeGroupInterval'] === 'function') {
										groupInterval = columnsMeasure[0]['dateTimeGroupInterval']();
									}

									const dateTimeFormat = columnsMeasure[0].dateTimeFormat;  // object holding formatting info
									const devExtremeFormat = this.getDevExtremeDateFormat(
										groupInterval,
										dateTimeFormat,
									);

									if (devExtremeFormat && devExtremeFormat[0]) {
										if (devExtremeFormat[1]) {
										}
									} else {
										columnConfig.dataType = 'string';

										if (groupInterval === '') {

										}
									}
								} catch (e) {
									console.error(
										'Error building dataType datetime, date',
										e,
									);
								}
							}

							if (dataType === 'number') {
								try {
									// Use the numeric format object
									let currencyCultureName = this.getIsoCurrencyFromLocale(columnsMeasure[0].numericFormat.currencyCultureName());
									columnConfig.dataType = 'number';
									columnConfig.format = {};

									if (columnsMeasure[0] && columnsMeasure[0].numericFormat && columnConfig.format) {
										let type = '';

										if (columnsMeasure[0].numericFormat.formatType()) {
											type = columnsMeasure[0].numericFormat.formatType();

											if (type.toLowerCase() === 'number') {
												columnConfig.format.type = 'fixedPoint';
											} else if (type.toLowerCase() === 'scientific') {
												columnConfig.format.type = 'exponential';
											} else if (type.toLowerCase() !== 'auto' && type.toLowerCase() !== 'general') {
												columnConfig.format.type = columnsMeasure[0].numericFormat.formatType();
											}
										}

										if (type.toLowerCase() !== 'auto' && type.toLowerCase() !== 'general') {
											if (columnsMeasure[0].numericFormat.precision()) {
												columnConfig.format.minimumSignificantDigits = columnsMeasure[0].numericFormat.precision();
											}

											let unit = columnsMeasure[0].numericFormat.unit();

											if (unit && type.toLowerCase() !== 'scientific' && unit.toLowerCase() !== 'auto'
												&& unit.toLowerCase() !== 'ones'
												&& type.toLowerCase() !== 'percent' && type.toLowerCase() !== 'auto') {
												columnConfig.format.type = unit;
											}

											if (type.toLowerCase() === 'number' && unit.toLowerCase() === 'ones') {
												// columnConfig.format.type = unit;
											}

											if (columnsMeasure[0].numericFormat.includeGroupSeparator() && type.toLowerCase() !== 'scientific') {
												columnConfig.format.useGrouping = columnsMeasure[0].numericFormat.includeGroupSeparator();
											}

											if (columnsMeasure[0].numericFormat.currencyCultureName() && currencyCultureName && type.toLowerCase() === 'currency') {
												columnConfig.format.currency = currencyCultureName;
											}

											if (columnsMeasure[0].numericFormat.precision()) {
												columnConfig.format.precision = columnsMeasure[0].numericFormat.precision();
												columnConfig.precision = columnsMeasure[0].numericFormat.precision();
											}
										}
									}
								} catch (e) {
									console.error(
										'Error building dataType number',
										e,
									);
								}
							}

							config.push(columnConfig);
						}
					}
				}

				const columnUniqueConfig: any = {
					dataField: 'DataKeyRowIndex',
					caption: 'DataKeyRowIndex',
					allowSorting: false,
					allowEditing: false,
					name: 'DataKeyRowIndex',
					visible: false,
				};
				config.push(columnUniqueConfig);
			}
		} catch (e) {
			console.error(
				'Error building columns config',
				e,
			);
		}

		return config ?? [];
	}

	getIsoCurrencyFromLocale(locale: string): string {
		try {
			if (!locale || locale.trim() === '') return '';
			const country = locale.split('-')[1]?.toUpperCase() || '';

			return countryToCurrency[country] || 'USD';
		} catch (error) {
			console.log(
				'getIsoCurrencyFromLocale, locale:',
				locale,
				error,
			);
		}

		return '';
	}

	getDevExtremeDateFormat(groupInterval: string, formatType: Model.DataItemDateTimeFormat | null): [boolean, string | undefined] {
		switch (groupInterval) {
			case 'Year':
				// If a specific format type is provided, use it
				if (formatType.yearFormat() === 'Abbreviated') {
					return [
						false,
						'yy',
					];
				}

				return [
					false,
					'yyyy',
				];
			case 'Quarter':
				// If a specific format type is provided, use it
				if (formatType.quarterFormat() === 'Numeric') {
					return [
						false,
						'Q',
					];
				}

				return [
					false,
					'\'Q\'Q',
				]; // Quarter + Year
			case 'Month':
				return [
					false,
					'MMMM yyyy',
				];
			case 'Day':
				return [
					false,
					'dd MMM yyyy',
				];
			case 'Hour':
				return [
					false,
					'HH',
				];
			case 'Minute':
				return [
					false,
					'HH:mm',
				];
			case 'Second':
				return [
					false,
					'HH:mm:ss',
				];
			case 'DayOfYear':
				// For complex groupings like Day Of Year, use custom cell template instead of format string
				return [
					false,
					undefined,
				];
			case 'DayOfWeek':
				return [
					false,
					'EEEE',
				]; // Long weekday name
			case 'WeekOfYear':
				return [
					false,
					undefined,
				]; // Requires custom template with week number calculation
			case 'WeekOfMonth':
				return [
					false,
					undefined,
				]; // Custom template needed
			case 'QuarterYear':
				return [
					false,
					'\'Q\'Q yyyy',
				];
			case 'MonthYear':
				return [
					false,
					'MMMM yyyy',
				];
			case 'WeekYear':
				return [
					true,
					undefined,
				]; // Custom template or formatted string with week+year
			case 'DayMonthYear':
				return [
					true,
					'dd-MM-yyyy',
				];
			case 'DateHour':
				return [
					true,
					'yyyy-MM-dd HH',
				];
			case 'DateHourMinute':
				return [
					true,
					'yyyy-MM-dd HH:mm',
				];
			case 'DateHourMinuteSecond':
				return [
					true,
					'yyyy-MM-dd HH:mm:ss',
				];
			case 'ExactDate':
				return [
					true,
					'yyyy-MM-dd HH:mm:ss',
				];
				// Add any additional custom intervals your dashboard supports below

			default:
				// Default fallback
				// if (formatTypeLower === 'long') return 'longDate';
				// if (formatTypeLower === 'short') return 'shortDate';
				return [
					true,
					undefined,
				]; // Use locale default
		}
	}

	addOrReplace(object) {
		// Check if the object has a unique ID
		const ids = this.getBindingValue('id') || [];
		// console.log("Current IDs:", ids, this._changedFcTableRecords);

		// Check if the object has a unique ID
		const index = this._changedFcTableRecords.findIndex((item: any) => {
			return ids.every((id) => {
				const key = id.uniqueName() + GRID_ID_IDENTIFIER;

				// Check if the key exists in both objects
				return item[key] === object[key];
			});
		});

		if (index === -1) {
			this._changedFcTableRecords.push(object);
		} else {
			this._changedFcTableRecords[index] = object;
		}
	}

	isRender = false;

	// Waits for a single binding to be ready (non-empty)
	private async waitForBinding(bindingName: string, timeout = 30000, interval = 300): Promise<boolean> {
		const start = Date.now();

		while (Date.now() - start < timeout) {
			try {
				let bindingVal = this.getBindingValue && this.getBindingValue(bindingName);
				if (bindingVal && bindingVal.length > 0) return true;
			} catch (e) {
				// ignore errors during attempt
			}

			await new Promise((resolve) => setTimeout(
				resolve,
				interval,
			));
		}

		console.error(`Binding "${bindingName}" not ready after ${timeout}ms for item ${this._customModel?.componentName()}`);

		return false;
	}

	// Waits for all bindings specified to be ready (default behavior)
	async ensureAllBindingsReady(...bindingNames: string[]): Promise<boolean> {
		const results = await Promise.all(bindingNames.map((name) => this.waitForBinding(name)));

		return results.every(Boolean);
	}

	// NEW: Waits for *any* binding in the list to be ready
	async waitForAnyBinding(bindingNames: string[], timeout = 2000, interval = 300): Promise<boolean> {
		const start = Date.now();

		while (Date.now() - start < timeout) {
			for (const name of bindingNames) {
				try {
					let bindingVal = this.getBindingValue && this.getBindingValue(name);
					if (bindingVal && bindingVal.length > 0) return true;
				} catch (e) {
					// ignore errors during attempt
				}
			}

			await new Promise((resolve) => setTimeout(
				resolve,
				interval,
			));
		}

		// console.error(`None of the bindings "${bindingNames.join(', ')}" were ready after ${timeout}ms for item ${this._customModel?.componentName()}`);
		return false;
	}

	isReloadCall: number = 0;
	override renderContent(element: HTMLElement, changeExisting: boolean) {
		// Exit early if model or its dataItems are not ready
		if (!this._customModel || !this._customModel.dataItems) {
			console.error('Render aborted, model is not ready.');

			return Promise.resolve();
		}

		let $this = this;

		return this.waitForAnyBinding([
			'columns',
			'rows',
			'id',
		])
			.then(async (bindingsReady) => {
				if (!bindingsReady) {
					DxDataGridItemExtension.instance.triggerDashboardReload(this.isReloadCall);
					this.isReloadCall = this.isReloadCall + 1;
					console.error(
						'Required bindings not available, aborting render.',
						this._customModel,
						this.isReloadCall,
						DxDataGridItemExtension,
						bindingsReady,
					);

					return;
				}

				const _columns = await this._getColumnsConfig() ?? [];
				let dataSource: any[] = [];

				try {
					dataSource = this._getDataSource();
				} catch (e) {
					console.error(
						'Error fetching data source',
						e,
					);
					dataSource = [];
				}

				const gridSettingsValues = this._customModel.customProperties.getValue('GridSettings') || '{}';
				const gridSettings = safelyParseJSON<GridSettings>(
					String(gridSettingsValues),
					{},
				);
				let ScrollingModeNative = gridSettings != null && gridSettings.ScrollingModeNative != null ? gridSettings.ScrollingModeNative : null;

				let ScrollingMode = gridSettings != null && gridSettings.ScrollingMode != null ? gridSettings.ScrollingMode : 'infinite';

				let OnSorting = gridSettings != null && gridSettings.OnSorting != null ? gridSettings.OnSorting : 'multiple';

				let EnableFilterRow = gridSettings != null && gridSettings.FilterRow != null ? gridSettings.FilterRow : true;

				let EnableHeaderFilter = gridSettings != null && gridSettings.HeaderFilter != null ? gridSettings.HeaderFilter : true;

				let EnableHeaderFilterAllowSearch = gridSettings != null && gridSettings.HeaderFilterAllowSearch != null ? gridSettings.HeaderFilterAllowSearch : true;

				let EnableHeaderFilterAllowSelectAll = gridSettings != null && gridSettings.HeaderFilterAllowSelectAll != null ? gridSettings.HeaderFilterAllowSelectAll : true;

				let HeaderFilterHeight = gridSettings != null && gridSettings.HeaderFilterHeight != null ? gridSettings.HeaderFilterHeight : 300;

				let EnableSearchPanel = gridSettings != null && gridSettings.SearchPanel != null ? gridSettings.SearchPanel : true;

				let SearchPanelHighlightCaseSensitive = gridSettings != null && gridSettings.SearchPanelHighlightCaseSensitive != null ? gridSettings.SearchPanelHighlightCaseSensitive : false;

				let SearchPanelHighlightSearchText = gridSettings != null && gridSettings.SearchPanelHighlightSearchText != null ? gridSettings.SearchPanelHighlightSearchText : true;

				let SearchPanelWidth = gridSettings != null && gridSettings.SearchPanelWidth != null ? gridSettings.SearchPanelWidth : 300;

				let SearchPanelPlaceholder = gridSettings != null && gridSettings.SearchPanelPlaceholder != null ? gridSettings.SearchPanelPlaceholder : 'Search...';

				let SelectedRowColor = gridSettings != null && gridSettings.SelectedRowColor != null ? gridSettings.SelectedRowColor : '#ffeee9';

				let selectedRowIndex: number | null = null;

				// Check if the grid is already initialized
				if (!changeExisting) {
					element.setAttribute(
						'data-item-component',
						this._customModel.componentName(),
					);
					element.innerHTML = ''; // clear container
					// Add your custom class to the grid container
					element.classList.add('customed-grid-dynamic-input');
					this.dxGridWidget = new dxDataGrid(
						element,
						{
							dataSource: dataSource,
							columns: _columns,
							keyExpr: 'DataKeyRowIndex', // Unique key for each row, required for selection and editing
							showBorders: true,
							allowColumnReordering: true,
							selection: {mode: 'single'},
							scrolling: {
								mode: ScrollingMode as 'standard' | 'virtual' | 'infinite',
								useNative: ScrollingModeNative === 'true' ? true : ScrollingModeNative === 'false' ? false : ScrollingModeNative === 'auto' ? 'auto' : undefined,
							},
							sorting: {
								mode: OnSorting as 'single' | 'multiple' | 'none', // Disable client-side sorting to preserve server order
							},
							searchPanel: {
								visible: EnableSearchPanel,
								highlightCaseSensitive: SearchPanelHighlightCaseSensitive,
								highlightSearchText: SearchPanelHighlightSearchText,
								searchVisibleColumnsOnly: false,
								placeholder: SearchPanelPlaceholder,
								width: SearchPanelWidth,
							},
							filterRow: {
								visible: EnableFilterRow,
								// applyFilter: 'auto',
								// showAllText: 'Show All',
								// resetOperationText: 'Reset Filter',
							},
							headerFilter: {
								visible: EnableHeaderFilter,
								allowSelectAll: EnableHeaderFilterAllowSelectAll,
								height: HeaderFilterHeight,
								search: {
									enabled: EnableHeaderFilterAllowSearch, // Updated way
								},
							},

							onRowClick: (e) => {
								if (e && e.rowType && e.rowType === 'data') {
									if (e.data && e.data.clientDataRow) {
										// Set the master filter based on the clicked row
										this.setMasterFilter(e.data.clientDataRow);
									}

									if (e) {
										selectedRowIndex = e.rowIndex;
										e.component.repaint();
									}
								}
							},
							onContentReady: (e) => {
								this._updateSelection();
							},
							onRowUpdating: (e) => {
								if ((e.key == undefined || e.key == null) || !e.newData) return;
								// Merge old and new data to get the full updated row
								const updatedRow = {
									...e.oldData,
									...e.newData,
								};
								this._unsavedChanges = true;
								this.addOrReplace(updatedRow);
							},
							editing: {
								allowUpdating: Boolean(this._customModel.customProperties.getValue('EnableEditableColumns')),
								mode: 'cell',
							},
							onEditorPreparing: (e) => {
								if (e.parentType === 'dataRow') {
									if (e.row.data && e.row.data.UserEdit != undefined && e.row.data.UserEdit != null && !e.row.data.UserEdit) {
										e.editorOptions.disabled = !e.row.data.UserEdit;
									}

									if (e.parentType === 'dataRow' && e.dataField) {
										if (e.editorOptions) {
											const originalOnValueChanged = e.editorOptions.onValueChanged;

											e.editorOptions.onValueChanged = (args: any) => {
												$this.handleValueChange(
													e,
													args.value,
												);

												// Call original handler if present
												if (originalOnValueChanged) {
													originalOnValueChanged(args);
												}
											};
										}
									}
								}
							},
							onEditorPrepared(event: any) {
								event.editorElement.addEventListener(
									'input',
									$this.debounce(
										(e: Event) => {
											const newValue = (e.target as HTMLInputElement).value;
											$this.handleValueChange(
												event,
												newValue,
											);
										},
										300,
									),
								);
							},
							onCellPrepared: (e) => {
								let styles = _columns.find((col: any) => col.dataField === e.column.dataField)?.style || '';
								// Always reset
								e.cellElement.style.backgroundColor = '';
								e.cellElement.classList.remove(
									'row-pink',
									'cell-yellow',
								);

								if (e.rowType === 'data' && styles) {
									e.cellElement.style.cssText = styles.toString();

									// 2. Selection or focus highlighting (yellow) && e.columnIndex === selectedColumnIndex
									if (e.rowIndex === selectedRowIndex) {
										e.cellElement.style.backgroundColor = SelectedRowColor;
									}
								}
							},
							// onRowValidating: e => {
							//     //console.log('Row validating event:', e);
							//     // if (/* key check fails */) {
							//     //     e.isValid = false;
							//     //     e.errorText = 'Row is missing key.';
							//     //     setTimeout(() => {
							//     //         e.component.closeEditCell(); // Close editor after error
							//     //     }, 0);
							//     // }
							// },
							// onRowPrepared(e: any) {
							//     // if (e.rowType === 'data' && e.isSelected) {
							//     //     e.rowElement.style.backgroundColor = '#ff0000'; // your highlight color
							//     // }
							// }
							// onCellClick: function (e) {
							//     if (EnableRowClick && e && e.column && !e.column.allowEditing) {
							//         selectedRowIndex = e.rowIndex;
							//         selectedColumnIndex = e.columnIndex;
							//         e.component.repaint();
							//     }
							// }
						},
					);

					this.dxGridWidget.option(
						'columns',
						_columns,
					);
					this.dxGridWidget.refresh();
				} else {
					this.dxGridWidget?.option({
						dataSource: dataSource,
						columns: _columns,
					});
				}
			})
			.catch((e) => {
				this.initializeAsync();
				console.error(
					'Error rendering grid content',
					e,
				);
			});
	}

	handleValueChange(event: any, newValue: any) {
		if (newValue !== event.row.data[event.dataField]) {
			let row = event.row.data;
			row[event.dataField] = newValue;
			this._unsavedChanges = true;
			const ids = this.getBindingValue('id') || [];
			const index = this._changedFcTableRecords.findIndex((item: any) => {
				return ids.every((id) => {
					const key = id.uniqueName() + GRID_ID_IDENTIFIER;

					// Check if the key exists in both objects
					return item[key] === row[key];
				});
			});

			event.setValue(newValue); // Required for grid to track the change

			if (index === -1) {
				this._changedFcTableRecords.push(row);
			} else {
				this._changedFcTableRecords[index] = row;
			}
		}
	}

	public debounce(func: Function, wait: number) {
		let timeout: any;

		return (...args: any[]) => {
			clearTimeout(timeout);
			timeout = setTimeout(
				() => func.apply(
					this,
					args,
				),
				wait,
			);
		};
	}

	override setSelection(values: Array<Array<any>>): void {
		super.setSelection(values);
		this._updateSelection();
	}

	private _updateSelection() {
		const gridInstance = this.dxGridWidget;

		if (gridInstance) {
			const selectedKeys = [];
			gridInstance.getDataSource().store()
				.load()
				.then((items: any[]) => {
					items.forEach((item, index) => {
						if (this.isSelected(item.clientDataRow)) {
							selectedKeys.push(item);
						}
					});
					gridInstance.selectRowsByIndexes(selectedKeys.map((_, i) => i));
				});
		}
	}

	override clearSelection(): void {
		super.clearSelection();
		this.dxGridWidget?.clearSelection();
	}

	override setSize(width: number, height: number): void {
		super.setSize(
			width,
			height,
		);
		this.dxGridWidget?.repaint();
	}

	override allowExportSingleItem(): boolean {
		return true;
	}

	override getExportInfo(): CustomItemExportInfo {
		return {
			image: undefined, // dxDataGrid does not render SVG, skip image export
		};
	}

	private async _initialize() {
		await this._getDashboardGridConfiguration();
		await this._getDashboardGridConfigurationDynamicUpdateObject();

		// this.renderContent(document.createElement('div'), false); // Ensure rendering happens after data is fetched
	}

	private async initializeAsync(): Promise<void> {
		setTimeout(
			() => {
			// this.renderContent(this.container, false);
			},
			100,
		); // adjust delay as necessary
	}

	_gridConfigurations: ListDto[] = [];
	private async _getDashboardGridConfiguration() {
		let _dashboardGridConfigurationData = await this.dashboardService.dashboardGridConfiguration();

		if (_dashboardGridConfigurationData != undefined || _dashboardGridConfigurationData != null) {
			_dashboardGridConfigurationData.forEach((item: ListDto) => {
				this._gridConfigurations.push(item);
			});
		}
	}

	_gridConfigurationsDynamicUpdateObject: ListDto[] = [];
	private async _getDashboardGridConfigurationDynamicUpdateObject() {
		let _dashboardGridConfigurationData = await this.dashboardService.LoadDynamicUpdateObject();

		if (_dashboardGridConfigurationData != undefined || _dashboardGridConfigurationData != null) {
			_dashboardGridConfigurationData.forEach((item: ListDto) => {
				this._gridConfigurationsDynamicUpdateObject.push(item);
			});
		}
	}

	override dispose(): void {
		const componentName = this._customModel.componentName();

		if (componentName) {
			DxDataGridItemExtension.instance.cleanupViewer(componentName);
		}

		super.dispose();
	}

	public componentName(): string {
		return this._customModel.componentName();
	}
}

export function safelyParseJSON<T>(value: string, fallback: T): T {
	try {
		return JSON.parse(value) as T;
	} catch {
		return fallback;
	}
}